import { useState } from 'react';
import { WEEKDAYS } from '../../api-seasons';
import { Button } from '../../ui';
import { INPUT, inZone } from '../event/shared';

/**
 * Picking games out of a season (D-371): all of them, one weekday, one
 * category (every Fireworks game), a date range, or by hand with the boxes on
 * each row. The quick picks replace the selection; the boxes then adjust it.
 */

export interface PickableGame {
  event_id: string;
  weekday: number | null;
  local_date: string | null;
  categories: { id: string; name: string }[];
}

export function QuickSelect({ games, onSelect }: { games: readonly PickableGame[]; onSelect: (ids: Set<string>) => void }) {
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const categories = [...new Map(games.flatMap((game) => game.categories).map((c) => [c.id, c.name])).entries()].sort((a, b) => a[1].localeCompare(b[1]));
  const pick = (test: (game: PickableGame) => boolean) => onSelect(new Set(games.filter(test).map((game) => game.event_id)));
  const days = [...new Set(games.map((game) => game.weekday).filter((d): d is number => d !== null))].sort();

  return (
    <div className="flex flex-wrap items-end gap-2 px-5 py-3 text-sm">
      <span className="text-xs font-semibold tracking-wide text-ink-400 uppercase">Select</span>
      <Button onClick={() => pick(() => true)}>All</Button>
      <Button onClick={() => onSelect(new Set())}>None</Button>
      {days.map((day) => (
        <Button key={day} onClick={() => pick((game) => game.weekday === day)} title={`Every ${WEEKDAYS[day]} game`}>
          {WEEKDAYS[day]}
        </Button>
      ))}
      {categories.length > 0 ? (
        <select
          aria-label="Every game in a category"
          value={category}
          onChange={(e) => {
            setCategory(e.target.value);
            if (e.target.value) pick((game) => game.categories.some((c) => c.id === e.target.value));
          }}
          className={INPUT}
        >
          <option value="">Category…</option>
          {categories.map(([id, name]) => (
            <option key={id} value={id}>
              {name}
            </option>
          ))}
        </select>
      ) : null}
      <input type="date" aria-label="From" value={from} onChange={(e) => setFrom(e.target.value)} className={INPUT} />
      <input type="date" aria-label="To" value={to} onChange={(e) => setTo(e.target.value)} className={INPUT} />
      <Button disabled={!from && !to} onClick={() => pick((game) => Boolean(game.local_date) && (!from || game.local_date! >= from) && (!to || game.local_date! <= to))}>
        Dates
      </Button>
    </div>
  );
}

/** "Fri May 14, 2027, 7:05 PM" in the game's own timezone. */
export const gameWhen = (startsAt: string | null, timezone: string | null) => (startsAt ? inZone(startsAt, timezone ?? 'America/Chicago') : 'No date');

/** Selections go to the API in groups this size, so one request never does too much. */
export function chunks<T>(items: readonly T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
